import { useCallback } from 'react';
import { Box, Center, SimpleGrid, Stack } from '@mantine/core';
import moment from 'moment';
import { useMask } from '@react-input/mask';

import { Button, DatePicker, Select, TextInput } from 'components';
import { CardRequestStep } from 'types';
import { AppConstants } from '../../../constants';

const { MASK_SWISS_PHONE_NUMBER, PHONE_NUMBER_CODES, PHONE_NUMBER_DATA } = AppConstants;

const PersonalInformationStep = ({
  form,
  onSubmit,
  options,
  t,
}: CardRequestStep) => {
  const phoneNumberRef = useMask({
    mask: MASK_SWISS_PHONE_NUMBER,
    replacement: { _: /\d/ },
  });

  const onSubmitWrapper = useCallback(() => {
    if (!isFormValid()) {
      return;
    }
    onSubmit();
  }, [form.values, options]);

  const isFormValid = useCallback(() => {
    const validations: {
      field: string;
      condition: boolean;
      error: string
    }[] = [
        { field: 'fullName', condition: !form.values.fullName, error: t('input.required') },
        { field: 'fullName', condition: form.values.fullName !== undefined && form.values.fullName.split(' ').length !== 2, error: t('input.fullName.invalid') },
        { field: 'phoneNumber', condition: !form.values.phoneNumber, error: t('input.required') },
        { field: 'phoneNumber', condition: form.values.phoneNumber !== undefined && !PHONE_NUMBER_CODES.some(code => form.values.phoneNumber.startsWith(code) && PHONE_NUMBER_DATA[code]?.length.includes(form.values.phoneNumber.replace(/\s/g, '').length)), error: t('input.phoneNumber.invalid') },
        { field: 'email', condition: !form.values.email, error: t('input.required') },
        { field: 'email', condition: form.values.email !== undefined && !/^\S+@\S+$/.test(form.values.email), error: t('input.email.invalid') },
      ];

    let isValid = true;
    validations.forEach(({ field, condition, error }) => {
      if (condition) {
        form.setFieldError(field, error);
        isValid = false;
      }
    });

    return isValid;
  }, [form.values, options]);

  return (
    <Center>
      <Box w={'80%'}>
        <SimpleGrid cols={{ base: 1, md: 2 }}>
          <Stack>
            <TextInput
              label={t('input.fullName.label')}
              placeholder={t('input.fullName.placeholder')}
              key={form.key('fullName')}
              {...form.getInputProps('fullName')}
            />
            <TextInput
              label={t('input.phoneNumber.label')}
              placeholder={t('input.phoneNumber.placeholder')}
              key={form.key('phoneNumber')}
              {...form.getInputProps('phoneNumber')}
              ref={phoneNumberRef}
            />
            <TextInput
              label={t('input.email.label')}
              placeholder={t('input.email.placeholder')}
              key={form.key('email')}
              {...form.getInputProps('email')}
            />
          </Stack>
          <Stack>
            <DatePicker
              label={t('input.birthDate.label')}
              placeholder={t('input.date.placeholder')}
              key={form.key('birthDate')}
              {...form.getInputProps('birthDate')}
              minDate={moment().subtract(65, 'years').calendar()}
              maxDate={moment().subtract(18, 'years').calendar()}
            />
            <Select
              label={t('input.nationality.label')}
              data={options['nationality']?.map(el => (
                { value: el, label: t(`input.nationality.options.${el}`) }
              ))}
              key={form.key('nationality')}
              {...form.getInputProps('nationality')}
              clearable={true}
            />
            {form.values.nationality === 'OTHER' ?
              <>
                <Select
                  label={t('input.visaType.label')}
                  data={options['visaType'].map(el => (
                    { value: el, label: t(`input.visaType.options.${el}`) }
                  ))}
                  key={form.key('visaType')}
                  {...form.getInputProps('visaType')}
                  clearable={true}
                />
              </>
              : undefined}
          </Stack>
        </SimpleGrid>
        <Button mt={'lg'} onClick={onSubmitWrapper}>
          {t('action.next')}
        </Button>
      </Box>
    </Center>
  )
}

export default PersonalInformationStep;